import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Mail, Smartphone, Mountain } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatAuthError } from "@/lib/api";

export default function RegistrationVerification({ registration, onRestart }) {
  const { startRegistrationPhone, verifyRegistrationPhone, clearPhoneOtp } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(registration);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stale, setStale] = useState(false);
  // A link may be opened in a second tab; read its verification progress here.
  useEffect(() => {
    let alive = true;
    const check = async () => {
      try {
        const { data } = await api.post("/auth/registration/status", { registration_token: registration.registration_token });
        if (alive) setProgress((previous) => ({ ...previous, ...data,
          email_verified: previous.email_verified || data.email_verified,
          phone_verified: previous.phone_verified || data.phone_verified,
          completed: previous.completed || data.completed }));
      } catch (err) {
        if (alive && err.response?.data?.detail === "invalid_or_expired_link") {
          setStale(true); setError("This signup has expired or was replaced. Start signup again and use the newest verification message.");
        }
      }
    };
    const timer = setInterval(check, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, [registration.registration_token]);
  async function send() {
    setBusy(true); setError(""); setCode("");
    try { await startRegistrationPhone(registration.registration_token); }
    catch { /* Keep OTP entry available; verification still requires a valid code. */ }
    finally { setBusy(false); }
  }
  async function confirm(event) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const result = await verifyRegistrationPhone(registration.registration_token, registration.phone, code);
      if (result?.pending_registration || result?.completed) {
        setProgress((previous) => ({ ...previous, ...result })); setCode("");
      } else {
        sessionStorage.removeItem("trailpulse:pending-registration"); navigate("/", { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = formatAuthError(err);
      if (err.code === "auth/invalid-verification-code" || detail === "invalid_or_expired_otp" || detail === "firebase_proof_required" || message === "Request a new code before verifying." || message === "Incorrect code. Please try again.") {
        setError("Incorrect OTP");
      } else if (err.code === "auth/code-expired" || err.code === "auth/session-expired" || message === "This code has expired. Request a new code.") {
        setError("OTP expired");
      } else {
        setError(message);
      }
    }
    finally { setBusy(false); }
  }
  return <div className="tp-app-shell text-[#F4EFEB]"><div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-6 py-12">
    <Link to="/landing" className="mb-7 flex items-center gap-3 font-bold"><Mountain className="h-8 w-8 text-[#E5B85C]" /> TrailPulse</Link>
    <section className="tp-dark-panel rounded-[2rem] p-7">
      <h1 className="text-2xl font-bold">{progress.completed ? "Account created" : "Verify to create your account"}</h1>
      <p className="mt-3 text-sm text-[#94A3B8]">{progress.completed ? "All your contact details are verified. You can now sign in." : registration.email && registration.phone ? "Your account isn’t created yet. Verify both your email and phone to finish signup." : `Your account isn’t created yet. Verify your ${registration.email ? "email" : "phone"} to finish signup.`}</p>
      {registration.email && <div className="mt-6 rounded-2xl border border-white/15 p-4"><h2 className="flex items-center gap-2 font-semibold"><Mail size={18} /> Email verification {progress.email_verified && <CheckCircle2 size={18} className="text-[#3FA98C]" />}</h2>
        <p className="mt-2 break-all text-sm">{registration.email}</p><p className="mt-2 text-sm text-[#94A3B8]">{progress.email_verified ? "Email verified." : registration.verification_delivery?.email?.sent ? "Open the link in your verification email. Check spam if it’s missing." : "The verification email could not be sent. Restart signup to send a new link."}</p>
      </div>}
      {registration.phone && <div className="mt-4 rounded-2xl border border-white/15 p-4"><h2 className="flex items-center gap-2 font-semibold"><Smartphone size={18} /> Phone verification {progress.phone_verified && <CheckCircle2 size={18} className="text-[#3FA98C]" />}</h2><p className="mt-2 text-sm">{registration.phone}</p>
        {progress.phone_verified ? <p className="mt-2 text-sm text-[#3FA98C]">Phone verified.{!progress.completed && registration.email ? " Verify your email to finish." : ""}</p> : <>
          <form onSubmit={confirm} className="mt-4"><label htmlFor="registration-code" className="text-sm">6-digit SMS code</label><input id="registration-code" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required disabled={busy || stale} className="mt-2 w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3" /><button type="submit" disabled={busy || stale || code.length !== 6} className="mt-3 min-h-11 w-full rounded-full bg-[#E5B85C] px-5 font-bold text-[#17211D] disabled:opacity-50">Verify phone</button></form>
          <button type="button" disabled={busy || stale} onClick={send} className="mt-4 min-h-10 rounded-full border border-white/20 px-5 font-semibold disabled:opacity-50">{busy ? "Please wait…" : "Resend code"}</button>
        </>}
      </div>}
      {error && <p role="alert" className="mt-4 text-sm text-[#E5695B]">{error}</p>}
      {!progress.completed && <button type="button" disabled={busy} onClick={() => { clearPhoneOtp(); onRestart(); }} className="mt-6 text-sm font-semibold text-[#E5B85C] underline">Change details or send a new email</button>}
      <Link to="/login" className="mt-5 block text-center text-sm text-[#94A3B8]">{progress.completed ? "Sign in" : "Already verified all details? Sign in"}</Link>
    </section>
  </div></div>;
}
